import { Injectable } from "@angular/core";
import { createFrom, EventService } from "@spartacus/core";
import { filter, map, Observable, pairwise, startWith, take, withLatestFrom } from "rxjs";
import { UserAccountFacade } from "@spartacus/user/account/root";
import { GonvarriUser } from "src/app/core/models/gonvarri-user.model";
import { NavigationEvent } from "@spartacus/storefront";
import { GonvarriNavigateToCheckoutEvent, GonvarriQuoteNavigateToCheckoutEvent } from "./gonvarri-checkout-navigation.events";
import { ActiveCartFacade } from "@spartacus/cart/base/root";
import { isAsturias } from "src/app/utils/check-environment";


@Injectable({ providedIn: 'root' })
export class GonvarriCheckoutNavigationEventBuilder {

  constructor(
      protected event: EventService,
      protected userAccountFacade: UserAccountFacade,
      protected activeCartService: ActiveCartFacade,
  ) {
    this.register();
  }

  protected register(): void {
    this.registerNavigateToCheckoutEvents();
  }

  protected registerNavigateToCheckoutEvents(): void {
    this.event.register(
      GonvarriNavigateToCheckoutEvent,
      this.buildNavigateToCheckoutEvent(),
    );
    this.event.register(
      GonvarriQuoteNavigateToCheckoutEvent,
      this.buildQuoteNavigateToCheckoutEvent(),
    );
  }

  protected buildNavigateToCheckoutEvent(): Observable<GonvarriNavigateToCheckoutEvent> {
    return this.event.get(NavigationEvent).pipe(
      startWith(<NavigationEvent><unknown>null),
      pairwise(),
      withLatestFrom(
        this.activeCartService.getActive(), // Get the active cart
        this.userAccountFacade.get().pipe( // Get the user details
          filter(user => !!user), 
          take(1)
      )),
    ).pipe(
      // Filter the navigation events to only get the ones that are navigating to the checkout page
      // Also, filter out the navigation events that are navigating FROM quote page, since we have a separate event for that below
      filter(([[prev, curr]]) => (!prev?.url.includes('checkout') && !prev?.url.includes('quote') && curr.url.includes('checkout'))),
      map(([, cart, user]) => {
      
        // Similar logic to the one present in FliegerCartPopulator.addTotalWeight method to calculate the total weight of the cart
        let totalWeight = 0;
    
        if (isAsturias()) {
          totalWeight = (cart as any).entries?.reduce((sum: number, entry: any) => {
            return sum + (entry.quantity! * (entry).packageWeight);
          }, 0);
        } else {
          totalWeight = cart.deliveryItemsQuantity ?? 0;
        }
      
        return createFrom(GonvarriNavigateToCheckoutEvent, {
          total_weight: totalWeight.toString(),
          total_price: cart.totalPrice?.value?.toString(),
          user_id: (user as GonvarriUser).orgUnit?.uid ?? '',
          user_email: user?.uid ?? '',
          total_tax: cart.totalTax?.value?.toString(),
          total_gross: cart.totalPriceWithTax?.value?.toString(),
        })
      })
    )
  }

  protected buildQuoteNavigateToCheckoutEvent(): Observable<GonvarriQuoteNavigateToCheckoutEvent> {
    return this.event.get(NavigationEvent).pipe(
      startWith(<NavigationEvent><unknown>null),
      pairwise(),
      withLatestFrom(
        this.activeCartService.getActive(), // Get the active cart
        this.userAccountFacade.get().pipe( // Get the user details
          filter(user => !!user), 
          take(1)
      )),
    ).pipe(
      // Filter the navigation events to only get the ones that are navigating to the checkout page FROM the quote page
      filter(([[prev, curr]]) => (prev?.url.includes('quote') && curr.url.includes('checkout'))),
      map(([navigationEvents, , user]) => {
        
        const quoteCode = navigationEvents[0].url.split('/').pop();
        return createFrom(GonvarriQuoteNavigateToCheckoutEvent, {
          user_id: (user as GonvarriUser).orgUnit?.uid ?? '',
          user_email: user?.uid ?? '',
          quotation_order_number: quoteCode,
        })
      })
    )
  }

}
